import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PaipanData, Pillar } from '../types';
import { convertText, getStemLabel, getBranchLabel } from '@/utils/langConvert';
import { calcFourPillars, STEM_ELEMENT, type FourPillars } from '@/utils/baziCalc';
import AiBaziReport from './AiBaziReport';
import type { SharedBaziDetailData } from '@/pages/paipan/sharedPaipanData';
import { buildBaziReportData, toPaipanExtras } from '@/utils/baziReportData';
import { getBasicInfoFixedLabels } from './basicInfoUi';

interface Props {
  data: PaipanData;
  pillars?: Pillar[];
  isDark?: boolean;
  fourPillars?: FourPillars;
  showAiReport?: boolean;
  detailData?: SharedBaziDetailData;
  initialChart?: 'composite' | 'birth';
}

export function getBasicInfoSurfaceStyle(isDark: boolean): string {
  if (isDark) return 'var(--bg-overlay-light)';
  return 'rgba(245,245,247,0.92)';
}

// ─── 命主信息国际化工具函数 ───────────────────────────────────────
const ZODIAC_I18N: Record<string, Record<string, string>> = {
  '鼠': { en: 'Rat', ko: '쥐', ja: '子(ね)' },
  '牛': { en: 'Ox', ko: '소', ja: '丑(うし)' },
  '虎': { en: 'Tiger', ko: '호랑이', ja: '寅(とら)' },
  '兔': { en: 'Rabbit', ko: '토끼', ja: '卯(うさぎ)' },
  '龍': { en: 'Dragon', ko: '용', ja: '辰(たつ)' },
  '龙': { en: 'Dragon', ko: '용', ja: '辰(たつ)' },
  '蛇': { en: 'Snake', ko: '뱀', ja: '巳(み)' },
  '馬': { en: 'Horse', ko: '말', ja: '午(うま)' },
  '马': { en: 'Horse', ko: '말', ja: '午(うま)' },
  '羊': { en: 'Goat', ko: '양', ja: '未(ひつじ)' },
  '猴': { en: 'Monkey', ko: '원숭이', ja: '申(さる)' },
  '雞': { en: 'Rooster', ko: '닭', ja: '酉(とり)' },
  '鸡': { en: 'Rooster', ko: '닭', ja: '酉(とり)' },
  '狗': { en: 'Dog', ko: '개', ja: '戌(いぬ)' },
  '豬': { en: 'Pig', ko: '돼지', ja: '亥(いのしし)' },
  '猪': { en: 'Pig', ko: '돼지', ja: '亥(いのしし)' },
};

function getLangKey(language: string): string {
  if (language === 'zh-HK') return 'zh-HK';
  if (language === 'zh-TW') return 'zh-TW';
  if (language.startsWith('zh')) return 'zh-CN';
  if (language.startsWith('ko')) return 'ko';
  if (language.startsWith('ja')) return 'ja';
  return 'en';
}

function formatZodiacForLocale(zodiac: string, language: string): string {
  const lang = getLangKey(language);
  if (lang === 'zh-CN' || lang === 'zh-HK') return zodiac;
  if (lang === 'zh-TW') return convertText(zodiac, language);
  const key = lang === 'ko' ? 'ko' : lang === 'ja' ? 'ja' : 'en';
  return ZODIAC_I18N[zodiac]?.[key] ?? zodiac;
}

function formatGenderForLocale(gender: string, language: string): string {
  const lang = getLangKey(language);
  const normalized = (gender || '').toLowerCase().trim();
  const isMale = normalized === '男' || normalized === 'male';
  const isFemale = normalized === '女' || normalized === 'female';
  if (lang === 'zh-CN' || lang === 'zh-HK' || lang === 'zh-TW') {
    if (isMale) return '男';
    if (isFemale) return '女';
    return gender || '';
  }
  if (lang === 'ko') return isMale ? '남성' : (isFemale ? '여성' : gender || '');
  if (lang === 'ja') return isMale ? '男性' : (isFemale ? '女性' : gender || '');
  if (isMale) return 'Male';
  if (isFemale) return 'Female';
  return gender || '';
}

function formatWangShuaiForLocale(val: string, language: string): string {
  const lang = getLangKey(language);
  if (lang === 'zh-CN' || lang === 'zh-HK') return val;
  if (lang === 'zh-TW') return convertText(val, language);
  const map: Record<string, Record<string, string>> = {
    '偏弱': { en: 'Slightly Weak', ko: '편약', ja: '偏弱' },
    '极弱': { en: 'Very Weak', ko: '극약', ja: '極弱' },
    '适中': { en: 'Balanced', ko: '균형', ja: '適中' },
    '旺盛': { en: 'Vigorous', ko: '왕성', ja: '旺盛' },
    '过旺': { en: 'Excess', ko: '과왕', ja: '過旺' },
    '身旺': { en: 'Strong Self', ko: '신왕', ja: '身旺' },
    '偏旺': { en: 'Slightly Strong', ko: '편왕', ja: '偏旺' },
    '身弱': { en: 'Weak Self', ko: '신약', ja: '身弱' },
    '偏强': { en: 'Slightly Strong', ko: '편강', ja: '偏強' },
    '身强': { en: 'Strong Self', ko: '신강', ja: '身強' },
    '极强': { en: 'Very Strong', ko: '극강', ja: '極強' },
    '从弱': { en: 'Follow Weak', ko: '종약', ja: '従弱' },
    '平和': { en: 'Balanced', ko: '평화', ja: '平和' },
  };
  const key = lang === 'ko' ? 'ko' : lang === 'ja' ? 'ja' : 'en';
  return map[val]?.[key] ?? val;
}

function formatYangYinForLocale(val: string, language: string): string {
  const lang = getLangKey(language);
  if (lang === 'zh-CN' || lang === 'zh-HK') return val;
  if (lang === 'zh-TW') return convertText(val, language);
  const map: Record<string, Record<string, string>> = {
    '阳男': { en: 'Yang Male', ko: '양남', ja: '陽男' },
    '阴男': { en: 'Yin Male', ko: '음남', ja: '陰男' },
    '阳女': { en: 'Yang Female', ko: '양녀', ja: '陽女' },
    '阴女': { en: 'Yin Female', ko: '음녀', ja: '陰女' },
    '阳盛': { en: 'Yang Dominant', ko: '양성', ja: '陽盛' },
    '偏阳': { en: 'Slightly Yang', ko: '편양', ja: '偏陽' },
    '平衡': { en: 'Balanced', ko: '균형', ja: '平衡' },
    '偏阴': { en: 'Slightly Yin', ko: '편음', ja: '偏陰' },
    '阴盛': { en: 'Yin Dominant', ko: '음성', ja: '陰盛' },
  };
  const key = lang === 'ko' ? 'ko' : lang === 'ja' ? 'ja' : 'en';
  return map[val]?.[key] ?? val;
}

function formatKongWangForLocale(kw: string, language: string): string {
  const lang = getLangKey(language);
  if (lang === 'zh-CN' || lang === 'zh-HK') return kw;
  if (lang === 'zh-TW') return convertText(kw, language);
  return kw.split('').map(c => {
    const stemLabel = getStemLabel(c, language);
    if (stemLabel !== c) return stemLabel;
    const branchLabel = getBranchLabel(c, language);
    if (branchLabel !== c) return branchLabel;
    return c;
  }).join('·');
}

function formatDayMasterXiForLocale(val: string, language: string): string {
  const lang = getLangKey(language);
  if (lang === 'zh-CN' || lang === 'zh-HK' || !val) return val;
  if (lang === 'zh-TW') return convertText(val, language);
  const stem = val[0];
  const translated = getStemLabel(stem, language);
  if (translated && translated !== stem) {
    const elementMap: Record<string, Record<string, string>> = {
      '水': { en: 'Water', ko: '수', ja: '水' },
      '木': { en: 'Wood', ko: '목', ja: '木' },
      '火': { en: 'Fire', ko: '화', ja: '火' },
      '土': { en: 'Earth', ko: '토', ja: '土' },
      '金': { en: 'Metal', ko: '금', ja: '金' },
    };
    const suffix = val.slice(1);
    const langKey = lang === 'ko' ? 'ko' : lang === 'ja' ? 'ja' : 'en';
    const translatedSuffix = elementMap[suffix]?.[langKey] ?? suffix;
    const separator = lang === 'en' ? ' ' : '';
    return `${translated}${separator}${translatedSuffix}`;
  }
  return val;
}

export function formatGanZhiPalaceForLocale(val: string | undefined, language: string): string {
  if (!val) return '';
  const lang = getLangKey(language);
  if (lang === 'zh-CN' || lang === 'zh-HK') return val;
  if (lang === 'zh-TW') return convertText(val, language);
  const stripped = val.replace(/（[^）]*）/g, '').replace(/\([^)]*\)/g, '').trim();
  if (!stripped) return '';
  return stripped.split('').map(c => {
    const stemLabel = getStemLabel(c, language);
    if (stemLabel !== c) return stemLabel;
    const branchLabel = getBranchLabel(c, language);
    if (branchLabel !== c) return branchLabel;
    return c;
  }).join('');
}

export function getRenYuanSiLingForLocale(val: string | undefined, language: string): string {
  if (!val) return '';
  const lang = getLangKey(language);
  if (lang === 'zh-CN' || lang === 'zh-HK') return val;
  if (lang === 'zh-TW') return convertText(val, language);
  const elementMap: Record<string, Record<string, string>> = {
    '木': { en: 'Wood', ko: '목', ja: '木' },
    '火': { en: 'Fire', ko: '화', ja: '火' },
    '土': { en: 'Earth', ko: '토', ja: '土' },
    '金': { en: 'Metal', ko: '금', ja: '金' },
    '水': { en: 'Water', ko: '수', ja: '水' },
  };
  const statusMap: Record<string, Record<string, string>> = {
    '旺半': { en: 'Half-Vigorous', ko: '반왕', ja: '半旺' },
    '旺': { en: 'Vigorous', ko: '왕', ja: '旺' },
    '相': { en: 'Active', ko: '상', ja: '相' },
    '休': { en: 'Resting', ko: '휴', ja: '休' },
    '囚': { en: 'Imprisoned', ko: '수', ja: '囚' },
    '死': { en: 'Dormant', ko: '사', ja: '死' },
    '用事': { en: 'In Command', ko: '용사', ja: '用事' },
  };
  const langKey = lang === 'ko' ? 'ko' : lang === 'ja' ? 'ja' : 'en';
  if (val.length >= 2) {
    const stem = val[0];
    const element = val[1];
    const status = val.slice(2);
    const translatedStem = getStemLabel(stem, language);
    const translatedElement = elementMap[element]?.[langKey] ?? element;
    const translatedStatus = statusMap[status]?.[langKey] ?? status;
    const separator = lang === 'en' ? ' ' : '';
    return `${translatedStem}${separator}${translatedElement}${separator}${translatedStatus}`;
  }
  return val;
}

export function getLunarDateForLocale(data: PaipanData, language: string): string {
  const lang = getLangKey(language);
  if (lang === 'zh-CN' || lang === 'zh-HK') return data.lunarDate;
  if (lang === 'zh-TW') return convertText(data.lunarDate, language);
  if (lang === 'ko') return data.lunarDate_ko || data.lunarDate;
  if (lang === 'ja') return data.lunarDate_ja || data.lunarDate;
  return data.lunarDate_en || data.lunarDate;
}

export function getJieQiDescForLocale(data: PaipanData, language: string): string {
  const lang = getLangKey(language);
  if (lang === 'zh-CN' || lang === 'zh-HK') return data.jieQiDesc || '';
  if (lang === 'zh-TW') return convertText(data.jieQiDesc || '', language);
  if (lang === 'ko') return data.jieQiDesc_ko || '';
  if (lang === 'ja') return data.jieQiDesc_ja || '';
  return data.jieQiDesc_en || '';
}

export function getBirthPlaceForLocale(data: PaipanData, language: string): string {
  const lang = getLangKey(language);
  if (lang === 'zh-CN' || lang === 'zh-HK') return data.birthPlace;
  if (lang === 'zh-TW') return convertText(data.birthPlace, language);
  if (lang === 'ko') return data.birthPlace_ko || data.birthPlace;
  if (lang === 'ja') return data.birthPlace_ja || data.birthPlace;
  return data.birthPlace_en || data.birthPlace;
}

export function getZodiacSignForLocale(data: PaipanData, language: string): string {
  const lang = getLangKey(language);
  if (lang === 'zh-CN' || lang === 'zh-HK') return data.zodiacSign || '';
  if (lang === 'zh-TW') return convertText(data.zodiacSign || '', language);
  if (lang === 'ko') return data.zodiacSign_ko || '';
  if (lang === 'ja') return data.zodiacSign_ja || '';
  return data.zodiacSign_en || '';
}

export function getConstellationForLocale(data: PaipanData, language: string): string {
  const lang = getLangKey(language);
  if (lang === 'zh-CN' || lang === 'zh-HK') return data.chineseConstellation || '';
  if (lang === 'zh-TW') return convertText(data.chineseConstellation || '', language);
  if (lang === 'ko') return data.chineseConstellation_ko || '';
  if (lang === 'ja') return data.chineseConstellation_ja || '';
  return data.chineseConstellation_en || '';
}

export function getKongWangForLocale(kw: string, language: string): string {
  return formatKongWangForLocale(kw, language);
}

const NAME_CHAR_ELEMENT: Record<string, string> = {
  林: '木', 木: '木', 森: '木', 杨: '木', 楊: '木', 柳: '木', 朱: '木', 梁: '木', 叶: '木', 葉: '木', 芳: '木', 英: '木', 莉: '木', 萍: '木', 兰: '木', 蘭: '木', 梅: '木', 桂: '木', 荣: '木', 榮: '木', 更: '木', 何: '木', 蔓: '木',
  陈: '火', 陳: '火', 张: '火', 張: '火', 李: '火', 丁: '火', 丹: '火', 晓: '火', 曉: '火', 丽: '火', 麗: '火', 光: '火', 炎: '火', 焱: '火', 煜: '火', 亮: '火', 南: '火',
  王: '土', 黄: '土', 黃: '土', 余: '土', 于: '土', 田: '土', 山: '土', 峰: '土', 坤: '土', 城: '土', 安: '土', 宇: '土', 伟: '土', 偉: '土', 佳: '土', 辰: '土',
  金: '金', 刘: '金', 劉: '金', 诗: '金', 詩: '金', 鑫: '金', 锦: '金', 錦: '金', 钧: '金', 鈞: '金', 铭: '金', 銘: '金', 新: '金', 刚: '金', 剛: '金', 利: '金', 锋: '金', 鋒: '金',
  水: '水', 江: '水', 河: '水', 海: '水', 洋: '水', 波: '水', 涛: '水', 明: '水', 雯: '水', 云: '水', 雲: '水', 雨: '水', 雪: '水', 沙: '水', 清: '水', 淼: '水', 涵: '水', 子: '水', 苗: '水',
  妈: '水', 媽: '水', 头: '火', 頭: '火', 狼: '火',
};

const RADICAL_ELEMENT_RULES: Array<{ chars: string; element: string }> = [
  { chars: '木艹竹禾', element: '木' },
  { chars: '火日灬光', element: '火' },
  { chars: '土山石田王', element: '土' },
  { chars: '钅釒金刂刀辛酉贝貝', element: '金' },
  { chars: '氵水雨冫子亥', element: '水' },
];

function inferNameCharElement(ch: string): string {
  if (NAME_CHAR_ELEMENT[ch]) return NAME_CHAR_ELEMENT[ch];
  const code = ch.codePointAt(0) || 0;
  if (code < 0x4e00 || code > 0x9fff) return '';
  const rule = RADICAL_ELEMENT_RULES.find(item => item.chars.split('').some(radical => ch.includes(radical)));
  return rule?.element || '';
}

function getNameElementText(name: string, language: string): string {
  const lang = getLangKey(language);
  if (lang === 'zh-TW') {
    const chars = (name || '').replace(/[^\u4e00-\u9fff]/g, '').split('');
    const elements = chars.map(inferNameCharElement).filter(Boolean).join('');
    const display = convertText(name || '', language);
    return elements ? `${display}（${elements}）` : display;
  }
  if (lang !== 'zh-CN' && lang !== 'zh-HK') return name || '';
  const chars = (name || '').replace(/[^\u4e00-\u9fff]/g, '').split('');
  const elements = chars.map(inferNameCharElement).filter(Boolean).join('');
  return elements ? `${name}（${elements}）` : name;
}

const YANG_STEMS_FOR_LABEL = new Set(['甲', '丙', '戊', '庚', '壬']);
function getZaoLabel(data: PaipanData): string {
  const isFemale = data.gender?.includes('女') || data.gender === 'female';
  const firstStem = (data.lunarDate || '').charAt(0);
  const yinYang = YANG_STEMS_FOR_LABEL.has(firstStem) ? '阳' : '阴';
  return `${yinYang} ${isFemale ? '坤造' : '乾造'}`;
}

const BAZI_ORBIT_BRANCHES = ['子', '丑', '寅', '卯', '辰', '巳', '午', '未', '申', '酉', '戌', '亥'];
const BAZI_ORBIT_STEMS = ['甲', '乙', '丙', '丁', '戊', '己', '庚', '辛', '壬', '癸'];
const ELEMENT_COLOR: Record<string, string> = {
  木: '#2FA64A',
  火: '#D14B45',
  土: '#A88300',
  金: '#E6A21A',
  水: '#2F86E9',
};
const BRANCH_ELEMENT: Record<string, string> = {
  寅: '木', 卯: '木',
  巳: '火', 午: '火',
  辰: '土', 戌: '土', 丑: '土', 未: '土',
  申: '金', 酉: '金',
  亥: '水', 子: '水',
};

const BRANCH_TO_ANGLE: Record<string, number> = {
  卯: -90, 寅: -60, 丑: -30, 子: 0, 亥: 30, 戌: 60,
  酉: 90, 申: 120, 未: 150, 午: 180, 巳: 210, 辰: 240,
};

const JIEQI_RING = ['清明', '春分', '惊蛰', '雨水', '立春', '大寒', '小寒', '冬至', '大雪', '小雪', '立冬', '霜降', '寒露', '秋分', '白露', '处暑', '立秋', '大暑', '小暑', '夏至', '芒种', '小满', '立夏', '谷雨'];
const JIEQI_TO_ANGLE = JIEQI_RING.reduce<Record<string, number>>((acc, term, idx) => {
  acc[term] = -90 + idx * 15;
  return acc;
}, {});
const YEAR_LABELS = ['年', '月', '日', '时'];
const YEAR_SUB_LABELS = ['木', '地\n公转', '月', '地\n自转'];
const ORBIT_ROLE_COLORS: Record<string, string> = {
  木: '#F2A05B',
  地: '#486C86',
  月: '#F2CC63',
};
const ORBIT_SIZE = 258;
const ORBIT_CENTER = ORBIT_SIZE / 2;

function getElementFromText(text: string | undefined): string {
  if (!text) return '';
  return ['木', '火', '土', '金', '水'].find(el => text.includes(el)) || '';
}

function shortDateTime(value: string | undefined): string {
  if (!value) return '--';
  return value.replace(/-/g, '年').replace(/^(\d{4})年(\d{2})年(\d{2})\s+(\d{2}:\d{2}).*$/, '$1年$2月$3日 $4');
}

function markerStyle(angleDeg: number, radius: number) {
  const angle = (angleDeg * Math.PI) / 180;
  return {
    left: `calc(50% + ${Math.cos(angle) * radius}px)`,
    top: `calc(50% + ${Math.sin(angle) * radius}px)`,
  };
}

function markerStyleWithOffset(angleDeg: number, radius: number, offsetX = 0, offsetY = 0) {
  const angle = (angleDeg * Math.PI) / 180;
  return {
    left: `calc(50% + ${Math.cos(angle) * radius + offsetX}px)`,
    top: `calc(50% + ${Math.sin(angle) * radius + offsetY}px)`,
  };
}

function getCircularDistanceClockwise(startAngle: number, endAngle: number): number {
  return (endAngle - startAngle + 360) % 360;
}

function getArcProps(startAngle: number, endAngle: number, radius: number) {
  const circumference = 2 * Math.PI * radius;
  const distance = getCircularDistanceClockwise(startAngle, endAngle);
  return {
    dasharray: `${(circumference * distance) / 360} ${circumference}`,
    rotation: startAngle,
  };
}

function getSolarTermAngle(termLabel: string): number {
  const matched = JIEQI_RING.find(term => termLabel.includes(term) || term.includes(termLabel));
  return matched ? JIEQI_TO_ANGLE[matched] : JIEQI_TO_ANGLE['立春'];
}

export default function BasicInfo({
  data,
  pillars = [],
  isDark = true,
  fourPillars,
  showAiReport = true,
  detailData,
  initialChart = 'composite',
}: Props) {
  const { t, i18n } = useTranslation('paipan');
  const isZh = i18n.language === 'zh-CN' || i18n.language === 'zh-TW' || i18n.language === 'zh-HK';
  const isNonCJK = i18n.language === 'en' || i18n.language === 'ko' || i18n.language === 'ja';
  const fixedLabels = getBasicInfoFixedLabels(i18n.language);

  const [activeChart, setActiveChart] = useState<'composite' | 'birth'>(initialChart);

  // ─── 主题色 ─────────────────────────────────────────────────
  const listBg = getBasicInfoSurfaceStyle(isDark);
  const listBorder = isDark ? 'rgba(183,154,197,0.2)' : 'rgba(232,160,32,0.22)';
  const rowBorder = isDark ? 'rgba(183,154,197,0.1)' : 'rgba(232,160,32,0.1)';
  const labelColor = 'var(--text-secondary)';
  const valueColor = 'var(--text-primary)';
  const accentColor = 'var(--accent-primary)';
  const goldColor = 'var(--accent-secondary)';
  const redColor = '#D14B45';

  const displayGender = formatGenderForLocale(data.gender || '男', i18n.language);
  const displayZodiac = formatZodiacForLocale(data.shengXiao || '', i18n.language);
  const displayWangShuai = formatWangShuaiForLocale(data.wangShuaiRef || '偏弱', i18n.language);
  const displayYangYin = formatYangYinForLocale(data.yangYinRef || '阳男', i18n.language);
  const displayLunarDate = getLunarDateForLocale(data, i18n.language);
  const displayBirthPlace = getBirthPlaceForLocale(data, i18n.language);
  const displayZodiacSign = getZodiacSignForLocale(data, i18n.language);
  const displayConstellation = getConstellationForLocale(data, i18n.language);
  const solarTermLangSuffix = i18n.language === 'en' ? '_en' : i18n.language === 'ko' ? '_ko' : i18n.language === 'ja' ? '_ja' : '';
  const rawPrevTermLabel = (
    data[`prevSolarTermName${solarTermLangSuffix}` as keyof PaipanData]
    || data.prevSolarTermName
    || t('basicInfo.field.jieTai')
  ) as string;
  const rawNextTermLabel = (
    data[`nextSolarTermName${solarTermLangSuffix}` as keyof PaipanData]
    || data.nextSolarTermName
    || t('basicInfo.field.qingMing')
  ) as string;
  const prevTermLabel = i18n.language === 'zh-TW' ? convertText(rawPrevTermLabel, i18n.language) : rawPrevTermLabel;
  const nextTermLabel = i18n.language === 'zh-TW' ? convertText(rawNextTermLabel, i18n.language) : rawNextTermLabel;
  const prevTermDate = data.prevSolarTermDate || data.jieTaiDate || '';
  const nextTermDate = data.nextSolarTermDate || data.qingMingDate || '';
  const displayYearKW  = formatKongWangForLocale(data.yearKongWang  || data.kongWang || '', i18n.language);
  const displayMonthKW = formatKongWangForLocale(data.monthKongWang || '', i18n.language);
  const displayDayKW   = formatKongWangForLocale(data.dayKongWang   || data.kongWang || '', i18n.language);
  const displayHourKW  = formatKongWangForLocale(data.hourKongWang  || '', i18n.language);
  const displayDayMasterXi = formatDayMasterXiForLocale(data.dayMasterXiXing || '', i18n.language);
  const displayJieQiDesc = getJieQiDescForLocale(data, i18n.language);
  const displayRenYuan = getRenYuanSiLingForLocale(data.renYuanSiLing, i18n.language);
  const displayTaiYuan = formatGanZhiPalaceForLocale(data.taiYuan, i18n.language);
  const displayTaiXi = formatGanZhiPalaceForLocale(data.taiXi, i18n.language);
  const displayMingGong = formatGanZhiPalaceForLocale(data.mingGong, i18n.language);
  const displayShenGong = formatGanZhiPalaceForLocale(data.shenGong, i18n.language);

  function getMingGuaForLocale(data: PaipanData, language: string): string {
    const lang = getLangKey(language);
    if (lang === 'zh-CN' || lang === 'zh-HK') return data.mingGua || '';
    if (lang === 'zh-TW') return convertText(data.mingGua || '', language);
    if (lang === 'ko') return data.mingGua_ko || data.mingGua || '';
    if (lang === 'ja') return data.mingGua_ja || data.mingGua || '';
    return data.mingGua_en || data.mingGua || '';
  }

  const displayMingGua = getMingGuaForLocale(data, i18n.language);
  const dayMasterElement = getElementFromText(data.dayMasterXiXing);
  const dayMasterColor = ELEMENT_COLOR[dayMasterElement] || accentColor;
  const skyLabelMonth = i18n.language === 'ko' ? '월령사령' : i18n.language === 'ja' ? '月令司令' : isZh ? '月令司令' : 'Month Command';
  const skySubMonth = i18n.language === 'ko' ? '왕쇠취기' : i18n.language === 'ja' ? '旺衰取気' : isZh ? '旺衰取气' : 'Season Qi';
  const skyLabelMansion = i18n.language === 'ko' ? '이십팔수' : i18n.language === 'ja' ? '二十八宿' : isZh ? '出生星宿' : 'Lunar Mansion';
  const skySubMansion = i18n.language === 'ko' ? '일치성수' : i18n.language === 'ja' ? '日値星宿' : isZh ? '值日星宿' : 'Day Mansion';
  const skyLabelGua = i18n.language === 'ko' ? '명괘방위' : i18n.language === 'ja' ? '命卦方位' : isZh ? '命卦方位' : 'Life Gua';
  const skySubGua = fixedLabels.eastWest;
  const baziSkyHighlights = [
    { label: skyLabelMonth, value: displayRenYuan, sub: skySubMonth, icon: 'ri-compass-3-line', color: goldColor },
    { label: skyLabelMansion, value: displayConstellation, sub: skySubMansion, icon: 'ri-star-line', color: accentColor },
    { label: skyLabelGua, value: displayMingGua, sub: skySubGua, icon: 'ri-compass-3-line', color: dayMasterColor },
  ];
  const baziSkyRows = [
    { label: prevTermLabel, value: prevTermDate },
    { label: fixedLabels.trueSolarTime, value: data.trueSolarTime || data.solarDate },
    { label: nextTermLabel, value: nextTermDate },
    { label: isZh ? '空亡' : 'Void', value: [displayYearKW, displayMonthKW, displayDayKW, displayHourKW].filter(Boolean).join(' / ') },
  ];
  const baziPalaceRows = [
    { label: t('basicInfo.field.taiYuan'), value: displayTaiYuan, color: '#D14B45' },
    { label: t('basicInfo.field.taiXi'), value: displayTaiXi, color: '#2F86E9' },
    { label: t('basicInfo.field.mingGong'), value: displayMingGong, color: goldColor },
    { label: t('basicInfo.field.shenGong'), value: displayShenGong, color: accentColor },
  ];
  const fallbackChartFp = (() => {
    if (fourPillars) return fourPillars;
    try {
      return calcFourPillars(data.trueSolarTime || data.solarDate, data.birthPlace);
    } catch {
      try {
        return calcFourPillars(data.solarDate, data.birthPlace);
      } catch {
        return null;
      }
    }
  })();
  const mainChartPillars = pillars.length >= 4
    ? pillars.slice(0, 4).map(pillar => ({
      stem: pillar.tianGan,
      branch: pillar.diZhi,
      stemColor: pillar.tianGanColor,
      branchColor: pillar.diZhiColor,
    }))
    : fallbackChartFp
      ? [fallbackChartFp.year, fallbackChartFp.month, fallbackChartFp.day, fallbackChartFp.hour].map(pillar => ({
        stem: pillar.stem,
        branch: pillar.branch,
        stemColor: ELEMENT_COLOR[STEM_ELEMENT[pillar.stem as keyof typeof STEM_ELEMENT] || ''],
        branchColor: ELEMENT_COLOR[BRANCH_ELEMENT[pillar.branch] || ''],
      }))
      : [];
  const chartPillars = mainChartPillars.map((pillar, idx) => ({
      ...pillar,
      label: YEAR_LABELS[idx],
      sub: YEAR_SUB_LABELS[idx],
      orbitRole: YEAR_SUB_LABELS[idx].split('\n')[0],
    }));
  const yearAngle = mainChartPillars[0] ? (BRANCH_TO_ANGLE[mainChartPillars[0].branch] ?? 180) : 180;
  const monthAngle = mainChartPillars[1] ? (BRANCH_TO_ANGLE[mainChartPillars[1].branch] ?? 30) : 30;
  const hourPillarAngle = mainChartPillars[3] ? (BRANCH_TO_ANGLE[mainChartPillars[3].branch] ?? 240) : 240;
  const liChunAngle = JIEQI_TO_ANGLE['立春'];
  const birthTermAngle = getSolarTermAngle(prevTermLabel);
  const jupiterArc = getArcProps(yearAngle, hourPillarAngle, 120);
  const selfRotationArc = getArcProps(birthTermAngle, liChunAngle, 82);
  const hourBranchLabel = mainChartPillars[3]?.branch || '时';
  const activeTermIdx = Math.max(0, JIEQI_RING.findIndex(item => prevTermLabel.includes(item) || item.includes(prevTermLabel)));

  return (
    <div className="px-4 pt-4 pb-8">

      {/* ─── 基本信息表 ─── */}
      <div
        className="rounded-2xl overflow-hidden mb-4"
        style={{ background: listBg, border: `1px solid ${listBorder}` }}
      >
        <Row2Col
          left={{ label: t('basicInfo.field.name'), value: getNameElementText(data.name, i18n.language) }}
          right={{ label: t('basicInfo.field.gender'), value: displayGender }}
          border={rowBorder} labelColor={labelColor} valueColor={valueColor}
        />
        <Row2Col
          left={{ label: t('basicInfo.field.lunar'), value: isZh ? `${displayLunarDate}（${convertText(getZaoLabel(data), i18n.language)}）` : displayLunarDate }}
          right={{ label: t('basicInfo.field.zodiac'), value: displayZodiac }}
          border={rowBorder} labelColor={labelColor} valueColor={valueColor}
        />
        <Row1Col
          label={t('basicInfo.field.solar')} value={data.solarDate}
          border={rowBorder} labelColor={labelColor} valueColor={valueColor}
        />
        <Row1Col
          label={t('basicInfo.field.trueSolar')} value={data.trueSolarTime}
          border={rowBorder} labelColor={labelColor}
          valueColor={'var(--accent-secondary)'}
        />
        <Row1Col
          label={t('basicInfo.field.birthPlace')} value={displayBirthPlace}
          border={rowBorder} labelColor={labelColor} valueColor={valueColor}
        />
        <div className="border-b px-3 py-2" style={{ borderColor: rowBorder, background: 'rgba(var(--accent-primary-rgb), 0.05)' }}>
          <p className="text-[10px] leading-relaxed" style={{ color: labelColor }}>
            {i18n.language === 'ko'
              ? '출생지와 출생 시진 모두 진태양시 보정에 영향을 미칩니다. 시진 경계에 가까울 경우 시주가 바뀔 수 있으며, 오행 에너지·격국·신살에도 영향을 줍니다.'
              : i18n.language === 'ja'
              ? '出生地と出生時辰はどちらも真太陽時の修正に影響します。時辰の境界に近い場合は時柱が変わる可能性があり、五行バランスや格局にも影響する場合があります。'
              : isZh
              ? '提示：出生地与出生时辰都会影响真太阳时修正；若临近时辰交界，可能改变时柱，并进一步影响五行能量、格局与神煞。'
              : 'Birth place and birth hour both affect true solar time; near hour boundaries they may change the hour pillar, element balance, pattern, and shensha.'}
          </p>
        </div>
        <Row1Col
          label={t('basicInfo.field.renYuan')} value={displayRenYuan}
          border={rowBorder} labelColor={labelColor}
          valueColor={isDark ? '#B79AC5' : '#3a3a3c'}
        />
        <Row1Col
          label={t('basicInfo.field.jieQi')}
          value={displayJieQiDesc}
          border={rowBorder} labelColor={labelColor} valueColor={valueColor}
          multiLine
        />
        <Row2Col
          left={{ label: prevTermLabel, value: prevTermDate }}
          right={{ label: nextTermLabel, value: nextTermDate }}
          border={rowBorder} labelColor={labelColor} valueColor={labelColor}
        />
        <Row2Col
          left={{ label: t('basicInfo.field.zodiacSign'), value: displayZodiacSign }}
          right={{ label: t('basicInfo.field.constellation'), value: displayConstellation }}
          border={rowBorder} labelColor={labelColor} valueColor={valueColor}
          smallValue
        />
        <div
          className="border-b"
          style={{ borderColor: rowBorder }}
        >
          <div className="flex items-center gap-2 px-3 py-2.5">
            <span className="text-[10px] flex-shrink-0 pt-0.5" style={{ color: labelColor, minWidth: 40 }}>{t('basicInfo.field.kongWang')}</span>
            <div className="flex flex-wrap gap-x-2 gap-y-0.5">
              <span className="text-xs leading-snug" style={{ color: redColor, fontFamily: "'Noto Serif TC', serif" }}>
                <span style={{ color: labelColor }} className="text-[9px] mr-0.5">{isZh ? '年' : ''}</span>{displayYearKW}
              </span>
              <span className="text-xs leading-snug" style={{ color: redColor, fontFamily: "'Noto Serif TC', serif" }}>
                <span style={{ color: labelColor }} className="text-[9px] mr-0.5">{isZh ? '月' : ''}</span>{displayMonthKW}
              </span>
              <span className="text-xs leading-snug" style={{ color: redColor, fontFamily: "'Noto Serif TC', serif" }}>
                <span style={{ color: labelColor }} className="text-[9px] mr-0.5">{isZh ? '日' : ''}</span>{displayDayKW}
              </span>
              <span className="text-xs leading-snug" style={{ color: redColor, fontFamily: "'Noto Serif TC', serif" }}>
                <span style={{ color: labelColor }} className="text-[9px] mr-0.5">{isZh ? '时' : ''}</span>{displayHourKW}
              </span>
            </div>
          </div>
        </div>
        <Row1Col
          label={t('basicInfo.field.mingGua')}
          value={displayMingGua}
          border={rowBorder}
          labelColor={labelColor}
          valueColor={valueColor}
        />
        <Row2Col
          left={{ label: t('basicInfo.field.taiYuan'), value: displayTaiYuan }}
          right={{ label: t('basicInfo.field.taiXi'), value: displayTaiXi }}
          border={rowBorder} labelColor={labelColor} valueColor={valueColor}
          smallValue
        />
        <Row2Col
          left={{ label: t('basicInfo.field.mingGong'), value: displayMingGong }}
          right={{ label: t('basicInfo.field.shenGong'), value: displayShenGong }}
          border={rowBorder} labelColor={labelColor} valueColor={valueColor}
          smallValue
          last
        />
      </div>

      {/* ─── 辅助参考 ─── */}
      <div
        className="rounded-2xl overflow-hidden mb-4"
        style={{ background: listBg, border: `1px solid ${listBorder}` }}
      >
        <div
          className="flex items-center gap-2 px-4 py-2.5 border-b"
          style={{ borderColor: rowBorder, background: isDark ? 'rgba(183,154,197,0.06)' : 'rgba(232,160,32,0.06)' }}
        >
          <div
            className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0"
            style={{ background: 'var(--accent-primary)' }}
          >
            <i className="ri-robot-line text-[0.5rem] text-white"></i>
          </div>
          <span
            className="text-xs font-bold"
            style={{ color: 'var(--accent-secondary)', fontFamily: "'Noto Serif TC', serif" }}
          >
            {t('basicInfo.assist.title')}
          </span>
        </div>

        <div className="grid grid-cols-2 border-b" style={{ borderColor: rowBorder }}>
          <div className="flex items-center gap-2 px-3 py-2.5 border-r" style={{ borderColor: rowBorder }}>
            <span className="text-[10px] flex-shrink-0" style={{ color: labelColor }}>{t('basicInfo.assist.dayMasterXi')}</span>
            <span
              className="text-xs font-bold"
              style={{ color: isDark ? '#4A90D9' : '#2255C0', fontFamily: "'Noto Serif TC', serif" }}
            >
              {displayDayMasterXi || (isNonCJK ? '' : '壬水')}
            </span>
          </div>
          <div className="flex items-center gap-2 px-3 py-2.5">
            <span className="text-[10px] flex-shrink-0" style={{ color: labelColor }}>{t('basicInfo.assist.yangYin')}</span>
            <span
              className="text-xs font-bold"
              style={{ color: goldColor, fontFamily: "'Noto Serif TC', serif" }}
            >
              {displayYangYin}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-2">
          <div className="flex items-center gap-2 px-3 py-2.5 border-r" style={{ borderColor: rowBorder }}>
            <span className="text-[10px] flex-shrink-0" style={{ color: labelColor }}>{t('basicInfo.assist.wangShuai')}</span>
            <span
              className="text-xs font-bold"
              style={{ color: isDark ? '#7BC67E' : '#2A7A3A', fontFamily: "'Noto Serif TC', serif" }}
            >
              {displayWangShuai}
            </span>
          </div>
          <div className="flex items-center gap-2 px-3 py-2.5">
            <span className="text-[10px] flex-shrink-0" style={{ color: labelColor }}>{t('basicInfo.assist.geJu')}</span>
            <span
              className="text-xs font-bold"
              style={{ color: accentColor, fontFamily: "'Noto Serif TC', serif" }}
            >
              {(() => {
                const lk = getLangKey(i18n.language);
                if (lk === 'zh-CN' || lk === 'zh-HK') return data.geJuRef || '食神格';
                if (lk === 'ko') return data.geJuRef_ko || data.geJuRef || '';
                if (lk === 'ja') return data.geJuRef_ja || data.geJuRef || '';
                return data.geJuRef_en || data.geJuRef || '';
              })()}
            </span>
          </div>
        </div>
      </div>

      {/* ─── 天时命卦综合盘 + 出生天体图 ─── */}
      {/* 切换按钮 */}
      <div className="flex items-center justify-center mb-3">
        <div
          className="flex rounded-full p-0.5"
          style={{
            background: isDark ? 'rgba(183,154,197,0.12)' : 'rgba(232,160,32,0.1)',
            border: `1px solid ${isDark ? 'rgba(183,154,197,0.2)' : 'rgba(232,160,32,0.2)'}`,
          }}
        >
          <button
            className="px-4 py-1.5 rounded-full text-[10px] font-medium transition-all"
            style={{
              background: activeChart === 'composite' ? (isDark ? 'rgba(183,154,197,0.28)' : 'rgba(232,160,32,0.22)') : 'transparent',
              color: activeChart === 'composite' ? (isDark ? '#E8C39E' : '#8B6914') : labelColor,
              fontFamily: "'Noto Serif TC', serif",
            }}
            onClick={() => setActiveChart('composite')}
          >
            {t('basicInfo.compositeChart.title')}
          </button>
          <button
            className="px-4 py-1.5 rounded-full text-[10px] font-medium transition-all"
            style={{
              background: activeChart === 'birth' ? (isDark ? 'rgba(183,154,197,0.28)' : 'rgba(232,160,32,0.22)') : 'transparent',
              color: activeChart === 'birth' ? (isDark ? '#E8C39E' : '#8B6914') : labelColor,
              fontFamily: "'Noto Serif TC', serif",
            }}
            onClick={() => setActiveChart('birth')}
          >
            {t('basicInfo.birthAstronomy.title')}
          </button>
        </div>
      </div>

      {activeChart === 'composite' && (
      <div
        className="rounded-2xl overflow-hidden mb-4"
        style={{ background: listBg, border: `1px solid ${listBorder}` }}
      >
        {/* 标题 */}
        <div
          className="flex items-center justify-between px-4 py-3 border-b"
          style={{ borderColor: rowBorder }}
        >
          <span
            className="text-xs font-bold"
            style={{ color: accentColor, fontFamily: "'Noto Serif TC', serif" }}
          >
            {t('basicInfo.compositeChart.title')}
          </span>
        </div>

        <div
          className="relative overflow-hidden"
          style={{
            height: 188,
            background: isDark
              ? 'radial-gradient(circle at center, rgba(183,154,197,0.18) 0, rgba(21,8,38,0.96) 58%, rgba(10,3,18,1) 100%)'
              : 'radial-gradient(circle at center, rgba(232,160,32,0.2) 0, rgba(255,250,240,0.98) 62%, rgba(250,241,221,1) 100%)',
          }}
        >
          <div
            className="absolute left-1/2 top-1/2 rounded-full"
            style={{
              width: 150,
              height: 150,
              transform: 'translate(-50%, -50%)',
              border: `1px solid ${isDark ? 'rgba(232,195,158,0.34)' : 'rgba(168,131,0,0.32)'}`,
              boxShadow: isDark ? '0 0 28px rgba(183,154,197,0.18)' : '0 0 22px rgba(232,160,32,0.16)',
            }}
          />
          <div
            className="absolute left-1/2 top-1/2 rounded-full"
            style={{
              width: 104,
              height: 104,
              transform: 'translate(-50%, -50%)',
              border: `1px dashed ${isDark ? 'rgba(183,154,197,0.36)' : 'rgba(168,131,0,0.28)'}`,
            }}
          />
          <div
            className="absolute left-1/2 top-1/2 rounded-full flex flex-col items-center justify-center"
            style={{
              width: 58,
              height: 58,
              transform: 'translate(-50%, -50%)',
              background: isDark ? 'rgba(12,5,25,0.78)' : 'rgba(255,251,242,0.86)',
              border: `1px solid ${dayMasterColor}66`,
            }}
          >
            <span className="text-[10px]" style={{ color: labelColor }}>{isZh ? '日主' : 'Self'}</span>
            <span className="text-lg font-bold leading-none" style={{ color: dayMasterColor, fontFamily: "'Noto Serif TC', serif" }}>
              {displayDayMasterXi || data.dayMaster || '日'}
            </span>
          </div>
          {BAZI_ORBIT_BRANCHES.map((branch, idx) => {
            const angle = ((idx * 30 - 90) * Math.PI) / 180;
            return (
              <span
                key={branch}
                className="absolute w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold"
                style={{
                  left: `calc(50% + ${Math.cos(angle) * 78}px - 10px)`,
                  top: `calc(50% + ${Math.sin(angle) * 78}px - 10px)`,
                  color: idx % 2 === 0 ? accentColor : goldColor,
                  background: isDark ? 'rgba(255,255,255,0.055)' : 'rgba(255,255,255,0.72)',
                  border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(168,131,0,0.12)'}`,
                  fontFamily: "'Noto Serif TC', serif",
                }}
              >
                {branch}
              </span>
            );
          })}
          {BAZI_ORBIT_STEMS.map((stem, idx) => {
            const angle = ((idx * 36 - 72) * Math.PI) / 180;
            return (
              <span
                key={stem}
                className="absolute text-[10px] font-bold"
                style={{
                  left: `calc(50% + ${Math.cos(angle) * 50}px - 5px)`,
                  top: `calc(50% + ${Math.sin(angle) * 50}px - 6px)`,
                  color: isDark ? 'rgba(232,195,158,0.72)' : 'rgba(118,82,0,0.76)',
                  fontFamily: "'Noto Serif TC', serif",
                }}
              >
                {stem}
              </span>
            );
          })}
          <div
            className="absolute left-3 bottom-3 right-3 flex items-center justify-between text-[10px]"
            style={{ color: labelColor, fontFamily: "'Noto Serif TC', serif" }}
          >
            <span>{prevTermLabel}</span>
            <span>{displayConstellation}</span>
            <span>{nextTermLabel}</span>
          </div>
        </div>

        {/* 说明文字 */}
        <div
          className="px-4 py-2.5 border-t border-b"
          style={{ borderColor: rowBorder, background: isDark ? 'rgba(183,154,197,0.04)' : 'rgba(232,160,32,0.04)' }}
        >
          <div className="flex items-start gap-2">
            <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
              <i className="ri-compass-3-line text-xs" style={{ color: accentColor }}></i>
            </div>
            <p
              className="text-[10px] leading-relaxed"
              style={{ color: labelColor, fontFamily: "'Noto Serif TC', serif" }}
            >
              {t('basicInfo.birthChart.desc')}
            </p>
          </div>
        </div>

        {/* ─── 八字天时摘要 ─── */}
        <div className="grid grid-cols-3 border-b" style={{ borderColor: rowBorder }}>
          {baziSkyHighlights.map((item, idx) => (
            <div
              key={item.label}
              className={`flex flex-col items-center py-3 px-1${idx < 2 ? ' border-r' : ''}`}
              style={{ borderColor: rowBorder }}
            >
              <div
                className="w-7 h-7 rounded-full flex items-center justify-center mb-1.5"
                style={{
                  background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
                  color: item.color,
                  border: `1px solid ${item.color}40`,
                }}
              >
                <i className={`${item.icon} text-sm`}></i>
              </div>
              <span className="text-[9px] mb-0.5" style={{ color: labelColor }}>
                {item.label}
              </span>
              <span
                className="text-[11px] font-bold text-center leading-tight"
                style={{ color: 'var(--text-primary)', fontFamily: "'Noto Serif TC', serif" }}
              >
                {item.value || '--'}
              </span>
              <span className="text-[9px] mt-0.5 text-center leading-tight" style={{ color: labelColor }}>
                {item.sub}
              </span>
            </div>
          ))}
        </div>

        {/* ─── 八字天时与辅助宫位 ─── */}
        <div className="px-4 pt-3 pb-3">
          <div className="flex items-center gap-1.5 mb-2">
            <div className="w-3 h-3 flex items-center justify-center">
              <i className="ri-star-line text-[10px]" style={{ color: accentColor }}></i>
            </div>
            <span
              className="text-[10px] font-bold"
              style={{ color: 'var(--accent-secondary)', fontFamily: "'Noto Serif TC', serif" }}
            >
              {fixedLabels.timeMarkers}
            </span>
          </div>
          <div className="grid grid-cols-2 gap-x-3 gap-y-1.5">
            {baziSkyRows.map((row) => (
              <div key={row.label} className="flex items-center gap-1">
                <span
                  className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                  style={{ background: accentColor }}
                />
                <span
                  className="text-[10px] flex-shrink-0"
                  style={{ color: labelColor, minWidth: isZh ? 42 : 62 }}
                >
                  {row.label}
                </span>
                <span
                  className="text-[10px] font-medium flex-1 truncate"
                  style={{ color: 'var(--text-primary)', fontFamily: "'Noto Serif TC', serif" }}
                >
                  {row.value || '--'}
                </span>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-1.5 mt-3 mb-2">
            <div className="w-3 h-3 flex items-center justify-center">
              <i className="ri-ancient-gate-line text-[10px]" style={{ color: accentColor }}></i>
            </div>
            <span
              className="text-[10px] font-bold"
              style={{ color: 'var(--accent-secondary)', fontFamily: "'Noto Serif TC', serif" }}
            >
              {fixedLabels.pillarReferences}
            </span>
          </div>
          <div className="grid grid-cols-2 gap-x-3 gap-y-1.5">
            {baziPalaceRows.map((row) => (
              <div key={row.label} className="flex items-center gap-1">
                <span
                  className="text-[10px] w-8 flex-shrink-0"
                  style={{ color: labelColor }}
                >
                  {row.label}
                </span>
                <span
                  className="text-[10px] font-medium flex-1 truncate"
                  style={{ color: row.color, fontFamily: "'Noto Serif TC', serif" }}
                >
                  {row.value || '--'}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
      )}

      {activeChart === 'birth' && (
      <div
        className="rounded-2xl overflow-hidden mb-4"
        style={{ background: listBg, border: `1px solid ${listBorder}` }}
      >
        <div
          className="flex items-center gap-2 px-4 py-3 border-b"
          style={{ borderColor: rowBorder }}
        >
          <span className="w-1 h-4 rounded-full" style={{ background: goldColor }} />
          <span className="text-xs font-bold" style={{ color: accentColor, fontFamily: "'Noto Serif TC', serif" }}>
            {t('basicInfo.birthAstronomy.title')}
          </span>
        </div>

        <div className="px-3 pt-4 pb-3">
          <div className="relative mx-auto" style={{ width: 258, height: 258 }}>
            <div className="absolute inset-0 rounded-full" style={{ border: `12px solid ${isDark ? 'rgba(232,195,158,0.56)' : 'rgba(240,171,124,0.68)'}` }} />
            <div className="absolute rounded-full" style={{ inset: 32, border: `14px solid ${isDark ? 'rgba(165,198,230,0.28)' : 'rgba(164,199,223,0.72)'}` }} />
            <div className="absolute rounded-full" style={{ inset: 66, border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(160,160,160,0.18)'}` }} />
            <svg
              className="absolute inset-0 pointer-events-none"
              width={ORBIT_SIZE}
              height={ORBIT_SIZE}
              viewBox={`0 0 ${ORBIT_SIZE} ${ORBIT_SIZE}`}
              aria-hidden="true"
            >
              <circle
                cx={ORBIT_CENTER}
                cy={ORBIT_CENTER}
                r={120}
                fill="none"
                stroke={isDark ? 'rgba(82,47,25,0.9)' : 'rgba(142,76,38,0.64)'}
                strokeWidth={12}
                strokeLinecap="butt"
                strokeDasharray={jupiterArc.dasharray}
                transform={`rotate(${jupiterArc.rotation} ${ORBIT_CENTER} ${ORBIT_CENTER})`}
              />
              <circle
                cx={ORBIT_CENTER}
                cy={ORBIT_CENTER}
                r={82}
                fill="none"
                stroke={isDark ? 'rgba(42,67,108,0.95)' : 'rgba(54,124,176,0.82)'}
                strokeWidth={14}
                strokeLinecap="butt"
                strokeDasharray={selfRotationArc.dasharray}
                transform={`rotate(${selfRotationArc.rotation} ${ORBIT_CENTER} ${ORBIT_CENTER})`}
              />
            </svg>
            {[
              { key: 'jupiter-start', angle: yearAngle, radius: 120 },
              { key: 'rotation-start', angle: liChunAngle, radius: 82 },
            ].map((dot) => (
              <span
                key={dot.key}
                className="absolute rounded-full"
                style={{
                  ...markerStyle(dot.angle, dot.radius),
                  width: 7,
                  height: 7,
                  transform: 'translate(-50%, -50%)',
                  background: '#fff',
                  border: `1px solid ${isDark ? 'rgba(255,255,255,0.86)' : 'rgba(255,255,255,0.95)'}`,
                  boxShadow: isDark ? '0 0 6px rgba(255,255,255,0.72)' : '0 0 5px rgba(90,65,35,0.28)',
                  zIndex: 6,
                }}
              />
            ))}
            {Array.from({ length: 12 }).map((_, idx) => {
              return (
                <span
                  key={idx}
                  className="absolute left-1/2 top-1/2 origin-left"
                  style={{
                    width: 108,
                    height: 1,
                    transform: `rotate(${idx * 30}deg)`,
                    background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.045)',
                    marginLeft: -54,
                    marginTop: 0,
                  }}
                />
              );
            })}
            {BAZI_ORBIT_BRANCHES.map((branch) => {
              const angle = BRANCH_TO_ANGLE[branch] ?? 0;
              const pos = markerStyle(angle, 120);
              return (
                <span
                  key={branch}
                  className="absolute -translate-x-1/2 -translate-y-1/2 text-sm font-bold"
                  style={{ ...pos, color: ['寅','卯','辰','巳','午','未'].includes(branch) ? '#D9916C' : labelColor, fontFamily: "'Noto Serif TC', serif" }}
                >
                  {branch}
                </span>
              );
            })}
            {JIEQI_RING.map((term, idx) => {
              const angle = -90 + idx * 15;
              const pos = markerStyle(angle, 78);
              return (
                <span
                  key={term}
                  className="absolute -translate-x-1/2 -translate-y-1/2 text-[7px] whitespace-nowrap"
                  style={{ ...pos, color: idx === activeTermIdx ? '#2F86E9' : labelColor }}
                >
                  {term}
                </span>
              );
            })}
            <div
              className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full flex items-center justify-center text-lg font-bold"
              style={{ width: 54, height: 54, background: isDark ? 'rgba(232,195,158,0.18)' : '#FFF0C7', color: valueColor, fontFamily: "'Noto Serif TC', serif" }}
            >
              日
            </div>
            <div
              className="absolute rounded-full border flex items-center justify-center text-[10px] font-bold"
              style={{ ...markerStyle(yearAngle, 112), width: 24, height: 24, transform: 'translate(-50%, -50%)', background: ORBIT_ROLE_COLORS['木'], color: '#1A0F1F', borderColor: ORBIT_ROLE_COLORS['木'], zIndex: 5 }}
            >
              木
            </div>
            <div
              className="absolute rounded-full border flex items-center justify-center text-[9px] font-bold"
              style={{ ...markerStyle(monthAngle, 90), width: 18, height: 18, transform: 'translate(-50%, -50%)', background: ORBIT_ROLE_COLORS['地'], color: '#fff', borderColor: isDark ? 'rgba(255,255,255,0.42)' : '#FFFFFF', zIndex: 4 }}
            >
              地
            </div>
            <div
              className="absolute rounded-full border flex items-center justify-center text-[10px] font-bold"
              style={{ ...markerStyleWithOffset(birthTermAngle, 82, 0, -8), width: 48, height: 48, transform: 'translate(-50%, -50%)', background: isDark ? 'rgba(95,128,180,0.34)' : 'rgba(202,225,245,0.95)', borderColor: isDark ? 'rgba(164,199,223,0.56)' : '#CFE2F3', color: valueColor, zIndex: 3 }}
            >
              {hourBranchLabel}
            </div>
            <div
              className="absolute rounded-full border flex items-center justify-center text-[9px] font-bold"
              style={{ ...markerStyle(hourPillarAngle, 72), width: 18, height: 18, transform: 'translate(-50%, -50%)', background: ORBIT_ROLE_COLORS['地'], color: '#fff', borderColor: isDark ? 'rgba(255,255,255,0.34)' : '#FFFFFF', zIndex: 4 }}
            >
              地
            </div>
          </div>

          <p className="text-[10px] text-center leading-relaxed mt-2" style={{ color: labelColor }}>
            {t('basicInfo.birthAstronomy.note')}
          </p>

          <div className="grid grid-cols-4 gap-2 mt-3">
            {chartPillars.map((pillar) => (
              <div key={pillar.label} className="rounded-xl py-2 flex flex-col items-center" style={{ background: isDark ? 'rgba(255,255,255,0.045)' : 'rgba(0,0,0,0.035)' }}>
                <span className="text-lg font-bold leading-none" style={{ color: pillar.stemColor || ELEMENT_COLOR[STEM_ELEMENT[pillar.stem as keyof typeof STEM_ELEMENT] || ''] || goldColor, fontFamily: "'Noto Serif TC', serif" }}>{pillar.stem}</span>
                <span className="text-lg font-bold leading-none mt-1" style={{ color: pillar.branchColor || ELEMENT_COLOR[BRANCH_ELEMENT[pillar.branch] || '土'] || '#D14B45', fontFamily: "'Noto Serif TC', serif" }}>{pillar.branch}</span>
                <span className="mt-1 text-[10px] w-5 h-5 rounded-full flex items-center justify-center" style={{ background: ORBIT_ROLE_COLORS[pillar.orbitRole] || accentColor, color: pillar.orbitRole === '月' ? '#3A2A00' : '#fff' }}>{pillar.orbitRole}</span>
                {pillar.sub.includes('\n') && <span className="text-[9px] mt-0.5" style={{ color: labelColor }}>{pillar.sub.split('\n')[1]}</span>}
              </div>
            ))}
          </div>

          <div className="rounded-xl border mt-3 px-3 py-2 space-y-1" style={{ borderColor: isDark ? 'rgba(232,195,158,0.24)' : 'rgba(168,131,0,0.22)', background: isDark ? 'rgba(255,255,255,0.025)' : 'rgba(255,255,255,0.55)' }}>
            <div className="flex gap-2 text-[10px]"><span style={{ color: labelColor, minWidth: 58 }}>{prevTermLabel}：</span><span style={{ color: valueColor }}>{shortDateTime(prevTermDate)}</span></div>
            <div className="flex gap-2 text-[10px]"><span style={{ color: goldColor, minWidth: 58 }}>{fixedLabels.birthSolar}：</span><span style={{ color: goldColor }}>{shortDateTime(data.trueSolarTime || data.solarDate)}</span></div>
            <div className="flex gap-2 text-[10px]"><span style={{ color: labelColor, minWidth: 58 }}>{nextTermLabel}：</span><span style={{ color: valueColor }}>{shortDateTime(nextTermDate)}</span></div>
          </div>
        </div>
      </div>
      )}

      {/* ─── AI 命理報告 ─── */}
      {showAiReport && (() => {
        const aiPerson = {
          name: data.name || '',
          gender: data.gender || '',
          birthTime: data.solarDate || '',
          lunarDate: data.lunarDate || '',
          trueSolarTime: data.trueSolarTime || '',
          birthPlace: data.birthPlace || '',
          fourPillars: fourPillars
            ? `${fourPillars.year.stem}${fourPillars.year.branch} ${fourPillars.month.stem}${fourPillars.month.branch} ${fourPillars.day.stem}${fourPillars.day.branch} ${fourPillars.hour.stem}${fourPillars.hour.branch}`
            : pillars.length >= 4
              ? pillars.slice(0, 4).map(p => `${p.tianGan}${p.diZhi}`).join(' ')
              : '',
        };
        const aiExtras = toPaipanExtras(data);
        const aiData = detailData
          ? buildBaziReportData(aiPerson, detailData, aiExtras)
          : undefined;
        return <AiBaziReport
          isDark={isDark}
          person={aiPerson}
          data={aiData}
        />;
      })()}

    </div>
  );
}

// ─── 辅助行组件 ─────────────────────────────────────────────────

interface ColItem {
  label: string;
  value: string;
}

interface Row2Props {
  left: ColItem;
  right: ColItem;
  border: string;
  labelColor: string;
  valueColor: string;
  smallValue?: boolean;
  last?: boolean;
}

function Row2Col({ left, right, border, labelColor, valueColor, smallValue, last }: Row2Props) {
  return (
    <div
      className="grid grid-cols-2"
      style={{ borderBottom: last ? 'none' : `1px solid ${border}` }}
    >
      <div className="flex items-start gap-1.5 px-3 py-2.5 border-r" style={{ borderColor: border }}>
        {left.label && (
          <span
            className="text-[10px] flex-shrink-0 pt-0.5"
            style={{ color: labelColor, minWidth: 40 }}
          >
            {left.label}
          </span>
        )}
        <span
          className={`${smallValue ? 'text-[10px]' : 'text-xs'} leading-snug`}
          style={{ color: valueColor, fontFamily: "'Noto Serif TC', serif" }}
        >
          {left.value}
        </span>
      </div>
      <div className="flex items-start gap-1.5 px-3 py-2.5">
        {right.label && (
          <span
            className="text-[10px] flex-shrink-0 pt-0.5"
            style={{ color: labelColor, minWidth: 24 }}
          >
            {right.label}
          </span>
        )}
        <span
          className={`${smallValue ? 'text-[10px]' : 'text-xs'} leading-snug`}
          style={{ color: valueColor, fontFamily: "'Noto Serif TC', serif" }}
        >
          {right.value}
        </span>
      </div>
    </div>
  );
}

interface Row1Props {
  label: string;
  value: string;
  border: string;
  labelColor: string;
  valueColor: string;
  multiLine?: boolean;
}

function Row1Col({ label, value, border, labelColor, valueColor, multiLine }: Row1Props) {
  return (
    <div
      className="flex items-start gap-2 px-3 py-2.5"
      style={{ borderBottom: `1px solid ${border}` }}
    >
      <span
        className="text-[10px] flex-shrink-0 pt-0.5"
        style={{ color: labelColor, minWidth: 56 }}
      >
        {label}
      </span>
      <span
        className="text-xs leading-snug flex-1"
        style={{
          color: valueColor,
          fontFamily: "'Noto Serif TC', serif",
          whiteSpace: multiLine ? 'pre-wrap' : undefined,
          wordBreak: 'break-all',
        }}
      >
        {value}
      </span>
    </div>
  );
}
